import React from 'react';
import { Typography, styled } from '@mui/material';
import Avatar from 'further-ui/components/Avatar';
import { Column, Row } from 'further-ui/components/Layout';
import { Controller, Control } from 'react-hook-form';
import { VehicleAccess, EditUserFormData } from '../../types';
import RadioButtons from 'further-ui/components/RadioButtons';
import Switch from 'further-ui/components/Switch';
import api from 'lib/trpcClient';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type Props = {
  control: Control<EditUserFormData>;
  vehicleAccess: VehicleAccess;
  selectedVehicleIds: Array<string>;
  onToggleVehicle: (vehicleId: string) => void;
  firmLogo?: string;
};

const vehicleAccessOptions = [
  { label: 'All vehicles', value: VehicleAccess.All },
  { label: 'Selected vehicles', value: VehicleAccess.Selected },
];

const VehicleToggleRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  border: `1px solid ${theme.palette.divider}`,
  borderBottom: 'none',

  '&:last-child': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const VehicleAccessSection: React.FC<Props> = ({
  control,
  vehicleAccess,
  selectedVehicleIds,
  onToggleVehicle,
  firmLogo,
}) => {
  const { data: vehicles, isLoading: isLoadingVehicles } =
    api.vehicle.listAllVehicles.useQuery({
      order: 'desc',
      orderBy: 'vehicleName',
    });

  const [hasAllVehiclesPermission] = useHasPermissions([
    RolePermission.LpAllVehiclesMember,
  ]);

  return (
    <Column spacing="lg">
      <Typography variant="h5">User vehicle access</Typography>

      <Controller
        name="vehicleAccess"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <RadioButtons
            options={vehicleAccessOptions}
            direction="row"
            value={field.value}
            onChange={field.onChange}
            error={!!error}
            helperText={error?.message}
            color="primary"
            disabled={!hasAllVehiclesPermission}
          />
        )}
      />

      {vehicleAccess === VehicleAccess.Selected && (
        <Column>
          {isLoadingVehicles ? (
            <Typography variant="body2">Loading vehicles...</Typography>
          ) : (
            vehicles?.result.map((vehicle) => (
              <VehicleToggleRow
                key={vehicle._id}
                centered
                justify="space-between"
              >
                <Row spacing="sm" centered>
                  <Avatar size="lg" src={firmLogo}>
                    {vehicle.vehicleName.charAt(0)}
                  </Avatar>
                  <Typography variant="h6">{vehicle.vehicleName}</Typography>
                </Row>
                <Switch
                  disabled={!hasAllVehiclesPermission}
                  checked={selectedVehicleIds.includes(vehicle._id)}
                  onChange={() => onToggleVehicle(vehicle._id)}
                />
              </VehicleToggleRow>
            ))
          )}
        </Column>
      )}
    </Column>
  );
};

export default VehicleAccessSection;
